
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './DonationForm.css';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import PaymentReceipt from '../Receipt/receipt';

const DonationForm = ({ showHeader = true }) => {
  const location = useLocation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: '' });
  const [showReceipt, setShowReceipt] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    amount: '',
    phone: '',
    address: '',
    panAadhaar: '',
    message: '',
    donationType: 'general'
  });

  useEffect(() => {
    if (location.state) {
      setFormData(prevData => ({
        ...prevData,
        ...location.state
      }));
    }
  }, [location.state]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));

    if (name === 'panAadhaar') {
      const upperValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      setFormData(prevData => ({
        ...prevData,
        [name]: upperValue
      }));
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.amount || !formData.phone || !formData.address || !formData.panAadhaar) {
      setSnackbar({
        open: true,
        message: 'Please fill in all the required fields.',
        severity: 'warning'
      });
      return;
    }

    try {
      const response = await fetch('https://razorpay-etechno.azurewebsites.net/api/payment/order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-functions-key': 'Sic_7V3eZdEGRU4lLRD8WYVcxeZDEA-_OJSfuHeO8NjRAzFuTL7xWA=='
        },
        body: JSON.stringify({
          amount: formData.amount,
          currency: 'INR',
          name: formData.name,
          email: formData.email
        }),
      });

      const { order } = await response.json();

      if (!order) {
        throw new Error('Failed to create order.');
      }

      const options = {
        key: 'rzp_live_yi1Ft5Wpd09ZPG',
        amount: order.amount,
        currency: order.currency,
        name: 'E-Techno Educational and Research Society',
        description: `Donation - ${formData.donationType}`,
        order_id: order.id,
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        handler: async (response) => {
          try {
            const verifyResponse = await fetch('https://razorpay-etechno.azurewebsites.net/api/payment/verify', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-functions-key': 'kArsn4YIynt9fxTwoSajarSHnX6TCrAlW_-9gdx9UifmAzFuF6dyMg=='
              },
              body: JSON.stringify(response),
            });

            const verifyResult = await verifyResponse.json();

            if (verifyResult.success) {
              setPaymentDetails({
                paymentId: response.razorpay_payment_id,
                ...formData
              });
              setShowReceipt(true);
            } else {
              setSnackbar({
                open: true,
                message: 'Payment verification failed!',
                severity: 'error'
              });
            }
          } catch (error) {
            setSnackbar({
              open: true,
              message: 'Error verifying payment.',
              severity: 'error'
            });
          }
        },
        theme: {
          color: '#3399cc',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: 'Error processing payment.',
        severity: 'error'
      });
    }
  };

  return (
    <div className="donation-form-container">
      {showReceipt && paymentDetails ? (
        <PaymentReceipt {...paymentDetails} />
      ) : (
        <>
          {showHeader && (
            <>
              <header className="donation-header">
                <h1>Make a Donation</h1>
              </header>

              <section className="donation-section">
                <h3>Support Our Cause</h3>
                <p>Your contribution helps us create positive change through education and research.</p>
              </section>
            </>
          )}

          <section className="donation-form">
            <h2>E-Techno Educational and Research Society</h2>
            <form onSubmit={handlePayment}>
              <div className="donation-form-group">
                <label htmlFor="donationType">Donation Type</label>
                <select
                  id="donationType"
                  name="donationType"
                  value={formData.donationType}
                  onChange={handleInputChange}
                  className="donation-form-control"
                >
                  <option value="general">General</option>
                  <option value="education">Cost of Education</option>
                  <option value="food">Cost of Food</option>
                  <option value="health">Cost of Medical</option>
                  <option value="water">Cost of Water</option>
                </select>
              </div>

              <div className="donation-form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="donation-form-control"
                  placeholder="Your Full Name"
                  required
                />
              </div>

              <div className="donation-form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="donation-form-control"
                  placeholder="Your Email"
                  required
                />
              </div>

              <div className="donation-form-group">
                <label htmlFor="amount">Donation Amount (INR)</label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  className="donation-form-control"
                  placeholder="Enter Amount"
                  required
                />
              </div>

              <div className="donation-form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="donation-form-control"
                  placeholder="Your Phone Number"
                  required
                />
              </div>

              <div className="donation-form-group">
                <label htmlFor="address">Address</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="donation-form-control"
                  placeholder="Your Address"
                  required
                />
              </div>

              <div className="donation-form-group">
                <label htmlFor="panAadhaar">PAN/Aadhaar Number</label>
                <input
                  type="text"
                  id="panAadhaar"
                  name="panAadhaar"
                  value={formData.panAadhaar}
                  onChange={handleInputChange}
                  className="donation-form-control"
                  placeholder="Enter PAN or Aadhaar Number"
                  pattern="([A-Z]{5}[0-9]{4}[A-Z]{1})|(\d{12})"
                  required
                />
              </div>

              <div className="donation-form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="donation-form-control"
                  placeholder="Your Message (Optional)"
                ></textarea>
              </div>

              <button type="submit" className="donation-form-submit-button">
                Proceed to Payment
              </button>
            </form>
          </section>

          <footer className="donation-footer">
            <p>Thank you for supporting our mission to empower education and research!</p>
            <p>&copy; 2024 E-Techno Educational and Research Society. All rights reserved.</p>
          </footer>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <MuiAlert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </MuiAlert>
          </Snackbar>
        </>
      )}
    </div>
  );
};

export default DonationForm;